import { createWebHistory, createRouter } from 'vue-router';
import {
  getTokens,
  signIn,
  currentSession,
  isStytchProvider,
  getCallbackToken,
  resolveSsoConnectionId,
  restoreSession,
} from '@/utils/auth';
import { useUsersStore } from '@/stores/useUsers';
import { hasAccess } from '@/composables/useNavigation';
import { useRbac } from '@/composables/useRbac';

import isFeatureFlagEnabled from '@/utils/featureFlags.js';
import { useEnvironment } from '@/composables/useEnvironment';
import {
  clearStaleBuildReload,
  isChunkLoadError,
  reloadForStaleBuild,
  setPendingNavigation,
} from '@/utils/appReload';

const publicRoutes = [
  '/login',
  '/notFound',
  '/notfound',
  '/unauthorized',
  '/forbidden',
];

async function validateAuth(to) {
  if (publicRoutes.includes(to.path)) return true;

  const id_token = sessionStorage.getItem('id_token');
  if (!id_token || id_token === 'undefined') {
    // A new tab starts with empty sessionStorage — see stytchProvider.restoreSession.
    if (await restoreSession()) return true;

    if (
      import.meta.env.VITE_LOCAL_AUTH === 'true' &&
      import.meta.env.MODE === 'desk'
    ) {
      return { name: 'login' };
    }
    return { name: 'unauthorized' };
  }
}

async function validatePermission(to) {
  try {
    // Check feature flag from route meta
    if (to.meta?.featureFlag) {
      const featureFlagPass = isFeatureFlagEnabled(to.meta.featureFlag);
      if (!featureFlagPass) return { name: 'forbidden' };
    }

    // Find allowedRoles from the current route or nearest parent
    let allowedRoles = to.meta?.allowedRoles;
    if (!allowedRoles) {
      for (const matched of to.matched) {
        if (matched.meta?.allowedRoles) {
          allowedRoles = matched.meta.allowedRoles;
          break;
        }
      }
    }

    const isAllowed = hasAccess(to.meta?.requiredSubscription, allowedRoles);
    if (!isAllowed) return { name: 'forbidden' };

    return true;
  } catch (err) {
    console.warn('Permission check failed:', err);
    return false;
  }
}

async function validateUserProfileAccess(to) {
  let users_store = useUsersStore();
  // A typed URL reaches this guard before App.vue has populated activeUser, so
  // the token is the fallback for the id.
  const token = currentSession() || {};
  let currentUser = users_store.activeUser || token;

  // Same VIEW_USER_PROFILE action that gates row clicks in UserTable.vue
  if (useRbac().hasWriteRole('VIEW_USER_PROFILE')) {
    return true;
  }

  // Check if contributor is viewing their own profile
  let routeId = Number(to.params.auxo_user_id);
  let currentUserId = Number(currentUser.auxo_user_id ?? token.auxo_user_id);

  if (routeId !== currentUserId) {
    return { name: 'forbidden' };
  }

  return true;
}

function validateNonProd() {
  if (useEnvironment().isProd()) {
    return { name: 'notfound' };
  }
}

// One-click login for the Okta tile: start SSO and cancel the navigation so the
// widget never renders. ?org= picks the organization on deployments offering
// more than one. Falls through to the login page when unresolvable — which is
// how a first admin signs in before any SSO connection exists.
async function handleSSOAutoLogin(to) {
  if (to.query.action !== 'sso-login') return;

  const connectionId = resolveSsoConnectionId(to.query.org);
  if (!connectionId) return;

  // Honour signIn's result rather than assuming it started: cancelling the
  // navigation when it did not would leave no login form at all.
  if (!(await signIn(connectionId))) return;
  return false;
}

// We have to have this bad larry here because if the router redirects to a
// route you don't have access too, validatePermission will send you to the 403 page
// This is for the edge case where users don't have access to /overview but have /details
function redirectToFirstAccessibleChild(to) {
  const children = to.matched[to.matched.length - 1]?.children ?? [];
  const target =
    children.find(child => hasAccess(child.meta?.requiredSubscription)) ??
    children[0];
  return `${to.path}/${target.path}`.replace(/\/+/g, '/');
}

// Send Prod users to 404 not found page if they try to access hosted UI
function useHostedUI() {
  if (isStytchProvider()) {
    return true;
  }

  if (
    import.meta.env.VITE_LOCAL_AUTH === 'true' &&
    import.meta.env.MODE === 'desk' &&
    !useEnvironment().isProd()
  ) {
    return true;
  }

  const useHostedUI = isFeatureFlagEnabled('HOSTED_UI');

  if (!useHostedUI) {
    return { name: 'notfound' };
  }
}

// ─── Route Access Gates ────────────────────────────────────────────────
//
// Routes are gated via `meta` properties, enforced by the `validatePermission`
// guard through `hasAccess(requiredSubscription, allowedRoles)` in useNavigation.js.
// Gates combine — all must pass:
//
//   allowedRoles: ['MGR', 'SUP']  — user must be in one listed Cognito group.
//     Roles: ADM (admin), REP (contributor), SUP (supporter), MGR (leader).
//     Children inherit the parent's roles unless they define their own.
//   requiredSubscription: ['HFMYXX']  — user must have one matching `auxo:subs`
//     JWT claim. If the claim is absent entirely, access is allowed (pre-rollout).
//   featureFlag: 'LEADER_HUB'  — hides the route when disabled; checked first.
//
// The same gates drive sidebar and tab-bar visibility: buildNavTree() and
// useTabBarItems() (useNavigation.js) filter via hasAccess() + isFeatureFlagEnabled(),
// so gated routes/tabs are automatically hidden from users who can't access them.
// Tabs inherit gates from the route they link to.
// ───────────────────────────────────────────────────────────────────────

export const routes = [
  {
    path: '/',
    redirect: '/lead',
  },
  {
    path: '/status',
    redirect: '/lead',
  },
  {
    path: '/login',
    name: 'login',
    beforeEnter: [useHostedUI, handleSSOAutoLogin],
    component: () => import('@/views/LoginView.vue'),
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/LogoutView.vue'),
  },
  {
    path: '/leader-hub',
    name: 'leaderHub',
    redirect: '/leader-hub/productivity',
    component: () => import('@/views/LeaderHubView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'Leader Hub',
      navIcon: 'sidebar-leader-hub',
      order: 5,
      allowedRoles: ['MGR', 'SUP'],
      testId: 'sidebar-dropdown-leaderhub',
      featureFlag: 'LEADER_HUB',
      requiredSubscription: ['LHPDOV', 'LHPDDT'],
    },
    children: [
      {
        path: 'productivity',
        name: 'productivity',
        redirect: redirectToFirstAccessibleChild,
        meta: {
          showInSidebar: true,
          navLabel: 'Productivity',
          order: 10,
          requiredSubscription: ['LHPDOV', 'LHPDDT'],
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/leader-hub/productivity/overview',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Detail',
              route: '/leader-hub/productivity/details',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: 'overview',
            name: 'productivityOverview',
            meta: {
              pageTitle: 'Productivity Overview',
              requiredSubscription: ['LHPDOV'],
            },
            component: () =>
              import('@hub/productivity/ProductivityOverviewTab.vue'),
          },
          {
            path: 'details',
            name: 'productivityDetails',
            meta: {
              pageTitle: 'Productivity Detail',
              requiredSubscription: ['LHPDDT'],
            },
            component: () =>
              import('@hub/productivity/ProductivityDetailsTab.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/ai-advisor',
    name: 'aiAdvisor',
    component: () => import('@/views/AiAdvisorView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'AI Advisor',
      navIcon: 'sidebar-ai-advisor',
      order: 7,
      allowedRoles: ['MGR'],
      testId: 'sidebar-item-ai-advisor',
      featureFlag: 'AI_ADVISOR',
      pageTitle: 'AI Advisor',
      requiredSubscription: ['AIADXX'],
    },
  },
  {
    path: '/lead',
    name: 'lead',
    component: () => import('@/views/HighFiveView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'High Five',
      navIcon: 'sidebar-high-five',
      order: 10,
      allowedRoles: ['ADM', 'REP', 'SUP', 'MGR'],
      testId: 'sidebar-item-lead',
    },
    children: [
      {
        path: '',
        name: 'leadDefault',
        meta: {
          pageTitle: 'My Work',
          tabBarItems: [
            { buttonText: 'My Work', route: '/lead', testId: 'tab-My Work' },
            {
              buttonText: 'Recent Items',
              route: '/lead/recent',
              testId: 'tab-Recent Items',
            },
          ],
        },
        component: () => import('@lead/tabs/LeadTab.vue'),
      },
      {
        path: 'recent',
        name: 'recent',
        meta: {
          pageTitle: 'Recent Items',
          tabBarItems: [
            { buttonText: 'My Work', route: '/lead', testId: 'tab-My Work' },
            {
              buttonText: 'Recent Items',
              route: '/lead/recent',
              testId: 'tab-Recent Items',
            },
          ],
          requiredSubscription: ['HFRWXX'],
        },
        component: () => import('@lead/tabs/RecentTab.vue'),
      },
      {
        path: ':id(\\d+)', // numeric only — rejects non-numeric work item IDs before guards run
        name: 'leadId',
        component: () => import('@lead/tabs/WorkItemDetailTab.vue'),
      },
    ],
  },
  {
    path: '/document/:claim(\\d+)', // numeric only — rejects non-numeric claim IDs before guards run
    name: 'document',
    meta: {
      allowedRoles: ['MGR', 'REP', 'ADM'],
      pageTitle: 'Appeal Letter',
      pageSubtitle: 'Customize your document below',
    },
    component: () => import('@/views/DocumentView.vue'),
    beforeEnter: [validateAuth, validatePermission],
  },
  {
    path: '/gameplan',
    name: 'gameplan',
    redirect: redirectToFirstAccessibleChild,
    component: () => import('@/views/GamePlanView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'Game Plan',
      navIcon: 'sidebar-gameplan',
      order: 20,
      allowedRoles: ['MGR', 'SUP'],
      testId: 'sidebar-item-gameplan',
      requiredSubscription: ['GPOVXX', 'GPWLXX', 'GPWLIO'],
      tabBarItems: [
        {
          buttonText: 'Overview',
          route: '/gameplan/overview',
          testId: 'tab-Overview',
        },
        {
          buttonText: 'Worklists',
          route: '/gameplan/worklists',
          testId: 'tab-Worklists',
        },
        {
          buttonText: 'Inflow & Outflow Details',
          route: '/gameplan/details',
          testId: 'tab-Inflow & Outflow Details',
        },
      ],
    },
    children: [
      {
        path: 'overview',
        name: 'gameplanOverview',
        meta: {
          pageTitle: 'Game Plan Overview',
          requiredSubscription: ['GPOVXX'],
        },
        component: () => import('@gameplan/tabs/GamePlanOverviewTab.vue'),
      },
      {
        path: 'worklists',
        name: 'gameplanWorklists',
        meta: {
          pageTitle: 'Worklists',
          requiredSubscription: ['GPWLXX'],
        },
        component: () => import('@gameplan/tabs/GamePlanWorklistsTab.vue'),
      },
      {
        path: 'worklists/new',
        name: 'newWorklist',
        meta: {
          pageTitle: 'Create Worklist',
          requiredSubscription: ['GPWLXX'],
        },
        component: () => import('@gameplan/worklists/UpsertWorkList.vue'),
      },
      {
        path: 'worklists/edit',
        name: 'editWorklist',
        meta: {
          pageTitle: 'Edit Worklist',
          requiredSubscription: ['GPWLXX'],
        },
        component: () => import('@gameplan/worklists/UpsertWorkList.vue'),
      },
      {
        path: 'details',
        name: 'gameplanDetails',
        meta: {
          pageTitle: 'Inflow & Outflow Details',
          requiredSubscription: ['GPWLIO'],
        },
        component: () => import('@gameplan/tabs/GamePlanDetailsTab.vue'),
      },
    ],
  },
  {
    path: '/users',
    name: 'users',
    meta: {
      showInSidebar: true,
      navLabel: 'Users',
      navIcon: 'sidebar-users',
      order: 30,
      allowedRoles: ['SUP', 'MGR', 'ADM'],
      testId: 'sidebar-item-users',
      pageTitle: 'Users',
      requiredSubscription: ['GPUMPR', 'GPUMSB'],
    },
    component: () => import('@/views/UsersView.vue'),
    beforeEnter: [validateAuth, validatePermission],
  },
  {
    path: '/users/:auxo_user_id(\\d+)', // numeric only — rejects non-numeric user IDs before guards run
    name: 'userDetail',
    redirect: redirectToFirstAccessibleChild,
    component: () => import('@/views/UserDetailsView.vue'),
    // Profile access is decided before the subscription gate so that an admin
    // is denied for the right reason regardless of the client's subscriptions.
    beforeEnter: [validateAuth, validateUserProfileAccess, validatePermission],
    meta: {
      requiredSubscription: ['GPUMPR', 'GPUMSB'],
      tabBarItems: [
        {
          buttonText: 'Profile',
          route: '/users/:auxo_user_id/profile',
          testId: 'tab-Profile',
        },
        {
          buttonText: 'Badges',
          route: '/users/:auxo_user_id/achievements',
          testId: 'tab-Badges',
        },
      ],
    },
    children: [
      {
        path: 'profile',
        name: 'userProfile',
        component: () => import('@users/profile/UserProfile.vue'),
        meta: {
          requiredSubscription: ['GPUMPR'],
        },
      },
      {
        path: 'achievements',
        name: 'userAchievements',
        meta: {
          pageTitle: 'Badges',
          requiredSubscription: ['GPUMSB'],
        },
        component: () => import('@users/achievements/UserAchievements.vue'),
      },
    ],
  },
  {
    path: '/stats',
    name: 'stats',
    component: () => import('@/views/StatsView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'Analytics',
      navIcon: 'sidebar-analytics',
      order: 40,
      allowedRoles: ['MGR'],
      testId: 'sidebar-dropdown-analytics',
      requiredSubscription: [
        'RPAROV',
        'RPARBO',
        'RPARHD',
        'RPARDT',
        'RPDNOV',
        'RPDNBO',
        'RPDNDT',
        'RPWAOV',
        'RPWABO',
        'RPWADT',
        'RPWGOV',
        'RPWGBO',
        'RPWGDT',
      ],
    },
    children: [
      {
        path: 'ar',
        name: 'ar',
        redirect: redirectToFirstAccessibleChild,
        meta: {
          showInSidebar: true,
          navLabel: 'AR',
          order: 10,
          allowedRoles: ['MGR'],
          statType: 'ar',
          store: 'arStatsStore',
          requiredSubscription: ['RPAROV', 'RPARBO', 'RPARHD', 'RPARDT'],
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/stats/ar/overview',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Breakout',
              route: '/stats/ar/breakout',
              testId: 'tab-Breakout',
            },
            {
              buttonText: 'High Dollar',
              route: '/stats/ar/high-dollar',
              testId: 'tab-High Dollar',
            },
            {
              buttonText: 'Detail',
              route: '/stats/ar/detail',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: 'overview',
            name: 'arOverview',
            meta: {
              pageTitle: 'AR Overview',
              pageSubtitle:
                'Average calculation does not include current month. Balance Bucket filter does not apply to metrics displayed on this page.',
              requiredSubscription: ['RPAROV'],
            },
            component: () => import('@stats/ar/ArOverview.vue'),
          },
          {
            path: 'breakout',
            name: 'arBreakout',
            meta: {
              pageTitle: 'AR Breakout',
              requiredSubscription: ['RPARBO'],
            },
            component: () => import('@stats/ar/ArBreakout.vue'),
          },
          {
            path: 'high-dollar',
            name: 'arHighDollar',
            meta: {
              pageTitle: 'AR High Dollar',
              requiredSubscription: ['RPARHD'],
            },
            component: () => import('@stats/ar/ArHighDollar.vue'),
          },
          {
            path: 'detail',
            name: 'arDetail',
            meta: {
              pageTitle: 'AR Detail',
              requiredSubscription: ['RPARDT'],
            },
            component: () => import('@stats/ar/ArDetail.vue'),
          },
        ],
      },
      {
        path: 'denials',
        name: 'denials',
        redirect: redirectToFirstAccessibleChild,
        meta: {
          showInSidebar: true,
          navLabel: 'Denials',
          order: 20,
          allowedRoles: ['MGR'],
          statType: 'denials',
          store: 'denialsStore',
          requiredSubscription: ['RPDNOV', 'RPDNBO', 'RPDNDT'],
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/stats/denials/overview',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Breakout',
              route: '/stats/denials/breakout',
              testId: 'tab-Breakout',
            },
            {
              buttonText: 'Detail',
              route: '/stats/denials/detail',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: 'overview',
            name: 'denialsOverview',
            meta: {
              pageTitle: 'Denials Overview',
              pageSubtitle: 'Date Filters cannot be applied to this page.',
              requiredSubscription: ['RPDNOV'],
            },
            component: () => import('@stats/denials/DenialsOverview.vue'),
          },
          {
            path: 'breakout',
            name: 'denialsBreakout',
            meta: {
              pageTitle: 'Denials Breakout',
              requiredSubscription: ['RPDNBO'],
            },
            component: () => import('@stats/denials/DenialsBreakout.vue'),
          },
          {
            path: 'detail',
            name: 'denialsDetail',
            meta: {
              pageTitle: 'Denials Detail',
              requiredSubscription: ['RPDNDT'],
            },
            component: () => import('@stats/denials/DenialsDetail.vue'),
          },
        ],
      },
      {
        path: 'workforce-management/activity',
        name: 'activity',
        redirect: redirectToFirstAccessibleChild,
        meta: {
          showInSidebar: true,
          navLabel: 'Activity',
          order: 30,
          allowedRoles: ['MGR'],
          statType: 'work_activity',
          store: 'activityStatsStore',
          requiredSubscription: ['RPWAOV', 'RPWABO', 'RPWADT'],
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/stats/workforce-management/activity/overview',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Breakout',
              route: '/stats/workforce-management/activity/breakout',
              testId: 'tab-Breakout',
            },
            {
              buttonText: 'Detail',
              route: '/stats/workforce-management/activity/detail',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: 'overview',
            name: 'activityOverview',
            meta: {
              pageTitle: 'Activity Overview',
              pageSubtitle:
                'Data displayed includes the "Complete" action type by default. This can be changed in the advanced filter menu.',
              requiredSubscription: ['RPWAOV'],
            },
            component: () => import('@stats/activity/ActivityOverview.vue'),
          },
          {
            path: 'breakout',
            name: 'activityBreakout',
            meta: {
              pageTitle: 'Activity Breakout',
              requiredSubscription: ['RPWABO'],
            },
            component: () => import('@stats/activity/ActivityBreakout.vue'),
          },
          {
            path: 'detail',
            name: 'activityDetail',
            meta: {
              pageTitle: 'Activity Detail',
              requiredSubscription: ['RPWADT'],
            },
            component: () => import('@stats/activity/ActivityDetail.vue'),
          },
        ],
      },
      {
        path: 'workforce-management/gap',
        name: 'workGap',
        redirect: redirectToFirstAccessibleChild,
        meta: {
          showInSidebar: true,
          navLabel: 'Work Gap',
          order: 40,
          allowedRoles: ['MGR'],
          statType: 'work_gap',
          store: 'workGapStore',
          requiredSubscription: ['RPWGOV', 'RPWGBO', 'RPWGDT'],
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/stats/workforce-management/gap/overview',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Breakout',
              route: '/stats/workforce-management/gap/breakout',
              testId: 'tab-Breakout',
            },
            {
              buttonText: 'Detail',
              route: '/stats/workforce-management/gap/detail',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: 'overview',
            name: 'workGapOverview',
            meta: {
              pageTitle: 'Work Gap Overview',
              requiredSubscription: ['RPWGOV'],
            },
            component: () => import('@stats/workgap/WorkGapOverview.vue'),
          },
          {
            path: 'breakout',
            name: 'workGapBreakout',
            meta: {
              pageTitle: 'Work Gap Breakout',
              requiredSubscription: ['RPWGBO'],
            },
            component: () => import('@stats/workgap/WorkGapBreakout.vue'),
          },
          {
            path: 'detail',
            name: 'workGapDetail',
            meta: {
              pageTitle: 'Work Gap Detail',
              requiredSubscription: ['RPWGDT'],
            },
            component: () => import('@stats/workgap/WorkGapDetail.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/devology',
    name: 'devology',
    redirect: '/devology/sandbox',
    component: () => import('@/views/DevologyView.vue'),
    beforeEnter: [validateAuth, validateNonProd],
    meta: {
      showInSidebar: true,
      navLabel: 'Devology',
      navIcon: 'sidebar-devology',
      order: 45,
      allowedRoles: ['ADM', 'REP', 'SUP', 'MGR'],
      testId: 'sidebar-item-devology',
      nonProdOnly: true,
      hiddenByDefault: true,
      tabBarItems: [
        {
          buttonText: 'Sandbox',
          route: '/devology/sandbox',
          testId: 'tab-Sandbox',
        },
      ],
    },
    children: [
      {
        path: 'sandbox',
        name: 'devologySandbox',
        meta: {
          pageTitle: 'Devology Sandbox',
        },
        component: () =>
          import('@/components/features/devology/DevologySandboxTab.vue'),
      },
    ],
  },
  {
    path: '/settings',
    name: 'settings',
    redirect: redirectToFirstAccessibleChild,
    component: () => import('@/views/SettingsView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInFooter: true,
      navLabel: 'Settings',
      navIcon: 'sidebar-settings',
      order: 50,
      allowedRoles: ['ADM'],
      testId: 'sidebar-item-settings',
      requiredSubscription: ['ADRPHD', 'ADUMED'],
      tabBarItems: [
        {
          buttonText: 'Admin Settings',
          route: '/settings/reports',
          testId: 'tab-Admin Settings',
        },
        {
          buttonText: 'Single Sign-On',
          route: '/settings/sso',
          testId: 'tab-Single Sign-On',
          featureFlag: 'SSO_ADMIN_PAGE',
        },
      ],
    },
    children: [
      {
        path: 'reports',
        name: 'reportAnalytics',
        meta: {
          pageTitle: 'Admin Settings',
          requiredSubscription: ['ADRPHD'],
        },
        component: () =>
          import('@/components/features/stats/ReportAnalyticsSettings.vue'),
      },
      {
        path: 'sso',
        name: 'ssoSettings',
        meta: {
          pageTitle: 'Single Sign-On',
          featureFlag: 'SSO_ADMIN_PAGE',
        },
        component: () =>
          import('@/components/features/sso/SsoAdminSettings.vue'),
      },
    ],
  },
  {
    name: 'notfound',
    path: '/notfound',
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      errorCode: 404,
      errorTitle: 'Page Not Found',
      errorMessage: "This page doesn't exist.",
      showHomeButton: true,
    },
  },
  {
    name: 'forbidden',
    path: '/forbidden',
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      errorCode: 403,
      errorTitle: 'Access Denied',
      errorMessage: "You don't have permission to access this resource.",
      showHomeButton: true,
    },
  },
  {
    name: 'unauthorized',
    path: '/unauthorized',
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      errorCode: 401,
      errorTitle: 'Authentication Required',
      errorMessage: 'Please log in to continue.',
      showHomeButton: false,
      showSignInButton: true,
    },
  },
  {
    name: 'catchAll',
    path: '/:catchAll(.*)', // matches any unmatched URL — catch-all 404 fallback
    beforeEnter: [validateAuth],
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      errorCode: 404,
      errorTitle: 'Page Not Found',
      errorMessage: "This page doesn't exist.",
      showHomeButton: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// vite:preloadError carries no route, so the destination is recorded here,
// ahead of the guards that might cancel it. See utils/appReload.js (CPRD-5916).
router.beforeEach(to => {
  setPendingNavigation(to.fullPath);
});

// Registering a handler silences the router's own console.error, so anything
// we don't act on has to be logged here or it is invisible.
router.onError((error, to) => {
  if (isChunkLoadError(error)) {
    reloadForStaleBuild(to?.fullPath);
    return;
  }
  console.error('Navigation failed:', error);
});

router.afterEach((to, from, failure) => {
  // Dropped even on failure, so a route a guard turned away cannot become a
  // later chunk failure's target. One that failed to load never gets here.
  setPendingNavigation('');

  // A completed navigation proves the current chunks are reachable, so the
  // one-shot is spent and a tab can still heal itself after a second deploy.
  if (failure) return;
  clearStaleBuildReload();
});

router.beforeEach(async to => {
  if (to.path === '/login' || to.path === '/logout') {
    const { useWebSockets } = await import('@/stores/useWebSockets');
    const ws_store = useWebSockets();
    if (ws_store.checkConnection) {
      await ws_store.close();
    }
  }
});

router.beforeEach(async (to, from) => {
  if (
    from.path === '/' &&
    to.path === '/lead' &&
    to?.redirectedFrom?.path === '/status'
  ) {
    const callbackToken = getCallbackToken(to.query);
    if (callbackToken) {
      try {
        await getTokens(callbackToken, null);
      } catch (err) {
        console.error('Token retrieval failed:', err);
        return { name: 'unauthorized' };
      }
    }
  }
});

// Drop the spent callback token from the URL (browser history, CloudFront
// cs-uri-query). Two constraints, both easy to get wrong — afterEach so the
// router does not overwrite it and redirectedFrom survives for App.vue's login
// audit, and location.pathname so a versioned blue/green base is preserved.
// See services/auxo-web/CLAUDE.md.
router.afterEach(to => {
  if (
    to.redirectedFrom?.path === '/status' &&
    (to.query.token || to.query.code)
  ) {
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname
    );
  }
});

export default router;
